import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Chip,
    Drawer,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    Tab,
    Tabs,
    Skeleton,
    Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Iconify from "../../components/iconify";
import copyFunction from "../../util/function/copyFunction";
import advService from "../../util/advService";
import { GET_EMPLOYEE_SUPPORT_GET } from "../../util/newconfig";

interface SupportContact {
    name: string;
    level: string;
    phone: string;
    email: string;
}

interface SupportData {
    broker: SupportContact[];
    tpa: SupportContact[];
    insurer: SupportContact[];
}

interface HelpContactCardProps {
    open: boolean;
    handleHelpContactOpen: () => void;
}

const HelpContactCard: React.FC<HelpContactCardProps> = ({
    open,
    handleHelpContactOpen,
}) => {
    const isMobile = useMediaQuery("(max-width:600px)");
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [supportData, setSupportData] = useState<SupportData | null>(null);
    const [loading, setLoading] = useState(false);
    const tabs = useMemo(
        () => [
            { label: "Broker", key: "Broker" },
            { label: "TPA", key: "Tpa" },
            { label: "HR", key: "Insurer" },
        ],
        []
    );

    const getAvailableTabs = useCallback(() => {
        if (!supportData) return [];
        return tabs.filter((tab) => {
            const tabKey = tab.key as keyof SupportData;
            const contacts = supportData[tabKey] || [];
            return contacts.some(
                (contact) => contact.name && contact.name.trim() !== ""
            );
        });
    }, [supportData, tabs]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };
    const fetchSupportData = () => {
        setLoading(true);
        advService(GET_EMPLOYEE_SUPPORT_GET, {}, (response: any) => {
            try {
                if (response.data.success) {
                    setSupportData(response.data.response);
                } else {
                    console.error(
                        "Error fetching support data:",
                        response.data.message
                    );
                }
            } catch (error) {
                console.error("Error processing support data:", error);
            } finally {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        if (open) {
            fetchSupportData();
        }
    }, [open]);

    useEffect(() => {
        const availableTabs = getAvailableTabs();
        if (availableTabs.length > 0 && activeTab >= availableTabs.length) {
            setActiveTab(0);
        }
    }, [supportData, activeTab, getAvailableTabs]);
    const getCurrentTabData = (): SupportContact[] => {
        if (!supportData) return [];
        const availableTabs = getAvailableTabs();
        if (availableTabs.length === 0) return [];

        const currentTab = availableTabs[activeTab];
        if (!currentTab) return [];

        const tabKey = currentTab.key as keyof SupportData;
        const contacts = supportData[tabKey] || [];
        return contacts.filter(
            (contact) => contact.name && contact.name.trim() !== ""
        );
    };
    const renderContactCard = (contact: SupportContact, index: number) => (
        <Box
            key={index}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                width: "100%",
                gap: "8px",
                marginBottom: "16px",
            }}
        >
            <Stack
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
            >
                <Typography variant="subtitle2">{contact.name}</Typography>
                <Chip
                    label={
                        <Typography
                            variant="label"
                            sx={{
                                color:
                                    contact.level === "1"
                                        ? theme.palette.error.dark
                                        : contact.level === "2"
                                        ? theme.palette.warning.dark
                                        : theme.palette.success.dark,
                            }}
                        >
                            {`Level ${contact.level}`}
                        </Typography>
                    }
                    sx={{
                        height: "18px",
                        width: "fit-content",
                        backgroundColor:
                            contact.level === "1"
                                ? theme.palette.error.lighter
                                : contact.level === "2"
                                ? theme.palette.warning.lighter
                                : theme.palette.success.lighter,
                    }}
                />
            </Stack>
            {[...contact.phone.split(","), ...contact.email.split(",")].map(
                (contactInfo, contactIndex) => (
                    <Stack
                        key={contactIndex}
                        flexDirection={"row"}
                        gap={"8px"}
                        alignItems={"center"}
                    >
                        <Box
                            sx={{
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Iconify
                                icon={"material-symbols:content-copy-outline"}
                                style={{
                                    cursor: "pointer",
                                    width: "20px",
                                    height: "20px",
                                    color: theme.palette.brand?.main,
                                }}
                                onClick={() => {
                                    copyFunction(contactInfo.trim());
                                }}
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            color={theme.palette.text.primary}
                        >
                            {contactInfo.trim()}
                        </Typography>
                    </Stack>
                )
            )}
        </Box>
    );

    const renderContactCardSkeleton = () => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                width: "100%",
                gap: "8px",
                marginBottom: "16px",
            }}
        >
            <Stack
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
            >
                <Skeleton variant="text" width={120} height={24} />
                <Skeleton
                    variant="rectangular"
                    width={60}
                    height={18}
                    sx={{ borderRadius: "9px" }}
                />
            </Stack>
            {[1, 2, 3].map((_, index) => (
                <Stack
                    key={index}
                    flexDirection={"row"}
                    gap={"8px"}
                    alignItems={"center"}
                >
                    <Box
                        sx={{
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Skeleton variant="circular" width={20} height={20} />
                    </Box>
                    <Skeleton variant="text" width={180} height={20} />
                </Stack>
            ))}
        </Box>
    );

    return (
        <Drawer
            open={open}
            onClose={handleHelpContactOpen}
            anchor={isMobile ? "bottom" : "right"}
            sx={{
                "& .MuiDrawer-paper": {
                    padding: "24px 20px",
                    display: "flex",
                    width: isMobile ? "100%" : "375px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flexShrink: 0,
                    borderRadius: {
                        xs: "24px 24px 0px 0px",
                        sm: "24px 0px 0px 24px",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "16px",
                    alignSelf: "stretch",
                    width: "100%",
                }}
            >
                <Stack
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    width={"100%"}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            color: theme.palette.text.primary,
                        }}
                        margin={0}
                    >
                        Connect with us
                    </Typography>
                    <Close
                        onClick={handleHelpContactOpen}
                        style={{ cursor: "pointer" }}
                        fontSize="medium"
                    />
                </Stack>
                {!isMobile && (
                    <Divider variant="dashed" sx={{ width: "100%" }} />
                )}
                {getAvailableTabs().length > 0 && (
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: 1,
                            borderColor: "divider",
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            textColor="primary"
                            indicatorColor="primary"
                            sx={{
                                "& .MuiTab-root": {
                                    textTransform: "none",
                                    fontWeight: 500,
                                },
                                "& .MuiTabs-indicator": {
                                    backgroundColor: theme.palette.brand.main,
                                },
                                "& .Mui-selected": {
                                    color: `${theme.palette.brand.main} !important`,
                                },
                            }}
                        >
                            {getAvailableTabs().map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Box>
                )}
                {getAvailableTabs().length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            padding: "16px 15px",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            gap: "16px",
                            alignSelf: "stretch",
                            flexDirection: "row",
                            width: "100%",
                            borderRadius: "16px",
                            backgroundColor: "#FFF3E4",
                        }}
                    >
                        <Iconify
                            icon={"material-symbols:warning-rounded"}
                            style={{
                                color: "#D5691B",
                                width: "32px",
                                height: "32px",
                            }}
                        />
                        <Typography
                            variant="overline"
                            sx={{
                                textTransform: "none",
                            }}
                        >
                            Please follow the given hierarchy only.
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "60vh",
                        overflowY: "auto",
                        paddingTop: "8px",
                    }}
                >
                    {loading ? (
                        <>
                            {[1, 2, 3].map((_, index) => (
                                <Box key={index}>
                                    {renderContactCardSkeleton()}
                                </Box>
                            ))}
                        </>
                    ) : (
                        getCurrentTabData().map((contact, index) =>
                            renderContactCard(contact, index)
                        )
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default HelpContactCard;
